import React, { useState, useMemo } from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useUpdateEffect } from 'react-use';
import { getTheme } from './css/theme';
import reducer from './modules';
import SiteWrapper from './containers/SiteWrapper';
import BotPage from './page/BotPage';
import CalendarPage from './page/CalendarPage';
import DiaryPage from './page/DiaryPage';
import HistoryPage from './page/HistoryPage';
import MainPage from './page/MainPage';
import ManagePage from './page/ManagePage';
import MessagePage from './page/MessagePage';
import NotFound from './page/NotFound';
import PhotoPage from './page/PhotoPage';
import PrivateManagePage from './page/PrivateManagePage';
import RobotPage from './page/RobotPage';
import SigninPage from './page/SigninPage';
import SignupPage from './page/SignupPage';
import SubUserPage from './page/SubUserPage';
import TermPage from './page/TermPage';
import VerificationPage from './page/VerificationPage';
import WifiPage from './page/WifiPage';
import DashboardPage from './page/DashboardPage';
import ProfilePage from './page/ProfilePage';
import SPDashboardPage from './page/SPDashboardPage';
import PicarePage from './page/PicarePage';

const store = configureStore({
  reducer,
  middleware: getDefaultMiddleware({
    serializableCheck: false,
  }),
});

const App = () => {
  const darkMode = localStorage.getItem('darkmode');
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)',
  ).matches;
  const [mode, setMode] = useState(
    darkMode || (prefersDarkMode ? 'dark' : 'light'),
  );
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = useMemo(() => getTheme(mode === 'dark'), [mode]);

  useUpdateEffect(() => {
    localStorage.setItem('darkmode', mode);
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <BrowserRouter>
          <CssBaseline />
          <LastLocationProvider>
            <Switch>
              <Route path="/manager" exact component={PrivateManagePage} />
              <Route
                path="/dashboard"
                exact
                component={(...rest) => (
                  <DashboardPage
                    {...{
                      ...rest,
                      darkMode: mode,
                      setColorMode: colorMode.toggleColorMode,
                    }}
                  />
                )}
              />
              <Route
                path="/sp"
                exact
                component={(...rest) => (
                  <SPDashboardPage
                    {...{
                      ...rest,
                      darkMode: mode,
                      setColorMode: colorMode.toggleColorMode,
                    }}
                  />
                )}
              />
              <Route path="/picare/:managerId" exact component={PicarePage} />
              <SiteWrapper
                darkMode={mode}
                setColorMode={colorMode.toggleColorMode}
              >
                <Route path="/" exact component={MainPage} />
                <Route path="/bot" exact component={BotPage} />
                <Route path="/bot/calendar" component={CalendarPage} />
                <Route path="/bot/diary" exact component={DiaryPage} />
                <Route path="/bot/history" exact component={HistoryPage} />
                <Route path="/manage" exact component={ManagePage} />
                <Route path="/bot/message" component={MessagePage} />
                <Route path="/bot/photo" exact component={PhotoPage} />
                <Route path="/robot" exact component={RobotPage} />
                <Route path="/signin" component={SigninPage} />
                <Route path="/signup" component={SignupPage} />
                <Route path="/profile" component={ProfilePage} />
                <Route path="/user" exact component={SubUserPage} />
                <Route path="/term" exact component={TermPage} />
                <Route
                  path="/user/verification/:type/:serial"
                  exact
                  component={VerificationPage}
                />
                <Route path="/wifi" exact component={WifiPage} />
              </SiteWrapper>
              <Route component={NotFound} />
            </Switch>
          </LastLocationProvider>
        </BrowserRouter>
      </Provider>
    </ThemeProvider>
  );
};

export default App;
